import React, { useContext } from "react";
import Table, {
  TableCell,
  TableRow,
  TableSectionHeader,
} from "@amzn/meridian/table";
import { FormattedMessage } from "react-intl";
import { PVAOverviewContext } from "../overview/context/PVAOverviewContext";

const PackageOverviewOutbound = () => {
  const { pvaData } = useContext(PVAOverviewContext);

  const getShiftRange = () => {
    return pvaData.pick.flowPVAData
      ? pvaData.pick.flowPVAData[
          pvaData.pvaTableData.granularity
        ].dataPointList.map((datapoint) => datapoint.timeStampVal)
      : [];
  };
  const getStowRequiredMap = () => {
    try {
      return pvaData.overviewData.flowPVAData[
        pvaData.pvaTableData.granularity
      ][3].dataPointList.reduce((map, obj) => {
        map[obj.timeStampVal] = obj.metricValue;
        return map;
      }, {});
    } catch (e) {
      return {};
    }
  };
  const getStowActualMap = () => {
    try {
      return pvaData.overviewData.flowPVAData[
        pvaData.pvaTableData.granularity
      ][2].dataPointList.reduce((map, obj) => {
        map[obj.timeStampVal] = obj.metricValue;
        return map;
      }, {});
    } catch (e) {
      return {};
    }
  };
  const getStowTargetPPHMap = () => {
    try {
      return pvaData.overviewData.flowPVAData[
        pvaData.pvaTableData.granularity
      ][0].dataPointList.reduce((map, obj, index) => {
        map[obj.timeStampVal] =
          pvaData.pvaTableData.granularity !== "cumulative"
            ? index % (60 / parseInt(pvaData.pvaTableData.granularity, 10)) ===
              0
              ? pvaData.targetData.STOW_PPH
              : ""
            : pvaData.targetData.STOW_PPH;
        return map;
      }, {});
    } catch (e) {
      return {};
    }
  };
  const getStowActualPPHMap = () => {
    try {
      return pvaData.performance[pvaData.pvaTableData.granularity].reduce(
        (map, obj) => {
          map[obj.time] = obj.stowPPH;
          return map;
        },
        {},
      );
    } catch (e) {
      return {};
    }
  };
  const getPickActualPPHMap = () => {
    try {
      return pvaData.performance[pvaData.pvaTableData.granularity].reduce(
        (map, obj) => {
          map[obj.time] = obj.pickPPH;
          return map;
        },
        {},
      );
    } catch (e) {
      return {};
    }
  };
  const getStageActualPPHMap = () => {
    try {
      return pvaData.performance[pvaData.pvaTableData.granularity].reduce(
        (map, obj) => {
          map[obj.time] = obj.stagePPH;
          return map;
        },
        {},
      );
    } catch (e) {
      return {};
    }
  };
  const shiftRange = getShiftRange();
  const stowRequired = getStowRequiredMap();
  const stowActual = getStowActualMap();
  const stowTargetPPH = getStowTargetPPHMap();
  const stowActualPPH = getStowActualPPHMap();
  const pickActualPPH = getPickActualPPHMap();
  const stageActualPPH = getStageActualPPHMap();

  return (
    <Table
      headerRows={1}
      showDividers={false}
      spacing="small"
      headerColumns={1}
      stickyHeaderColumn={true}
      stickyHeaderRow={true}
      fixHeaderRows={true}
    >
      {/* Shift times [sort, stage]*/}
      <TableRow>
        <TableCell />
        {shiftRange.map((timeStampVal, index) => (
          <TableCell key={index}>
            {new Date(timeStampVal).toTimeString().split(" ")[0].split(":")[0] +
              ":" +
              new Date(timeStampVal).toTimeString().split(" ")[0].split(":")[1]}
          </TableCell>
        ))}
      </TableRow>

      {/* PICK */}
      <TableSectionHeader>
        <FormattedMessage id="package_overview.pick" defaultMessage="Pick" />
      </TableSectionHeader>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.required"
            defaultMessage="Required"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.actual"
            defaultMessage="Actual"
          />
        </TableCell>
        {pvaData.pick.flowPVAData
          ? pvaData.pick.flowPVAData[
              pvaData.pvaTableData.granularity
            ].dataPointList.map((datapoint, index) => (
              <TableCell key={index}>{datapoint.metricValue}</TableCell>
            ))
          : null}
      </TableRow>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.target_pph"
            defaultMessage="Target PPH"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.actual_pph"
            defaultMessage="Actual PPH"
          />
        </TableCell>
        {shiftRange.map((timeStampVal) => {
          const time = new Date(timeStampVal);
          return (
            <TableCell key={timeStampVal}>
              {pickActualPPH[time] === -1 || !pickActualPPH[time]
                ? ""
                : pickActualPPH[time].toFixed(0)}
            </TableCell>
          );
        })}
      </TableRow>

      {/* STOW */}
      <TableSectionHeader>
        <FormattedMessage id="package_overview.stow" defaultMessage="Stow" />
      </TableSectionHeader>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.required"
            defaultMessage="Required"
          />
        </TableCell>
        {shiftRange.map((timeStampVal, index) => (
          <TableCell key={index}>
            {stowRequired[timeStampVal] < 0 || !stowRequired[timeStampVal]
              ? stowRequired[timeStampVal] === -1
                ? "-"
                : "0"
              : stowRequired[timeStampVal]}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.actual"
            defaultMessage="Actual"
          />
        </TableCell>
        {shiftRange.map((timeStampVal, index) => (
          <TableCell key={index}>
            {stowActual[timeStampVal] < 0 || !stowActual[timeStampVal]
              ? stowActual[timeStampVal] === -1
                ? "-"
                : "0"
              : stowActual[timeStampVal]}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.target_pph"
            defaultMessage="Target PPH"
          />
        </TableCell>
        {shiftRange.map((timeStampVal, index) => (
          <TableCell key={index}>
            {stowTargetPPH[timeStampVal] < 0 || !stowTargetPPH[timeStampVal]
              ? stowTargetPPH[timeStampVal] === -1
                ? "-"
                : "0"
              : stowTargetPPH[timeStampVal]}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.actual_pph"
            defaultMessage="Actual PPH"
          />
        </TableCell>
        {shiftRange.map((timeStampVal) => {
          const time = new Date(timeStampVal);
          return (
            <TableCell key={timeStampVal}>
              {stowActualPPH[time] === -1 || !stowActualPPH[time]
                ? ""
                : stowActualPPH[time].toFixed(0)}
            </TableCell>
          );
        })}
      </TableRow>

      {/* STAGE */}
      <TableSectionHeader>
        <FormattedMessage id="package_overview.stage" defaultMessage="Stage" />
      </TableSectionHeader>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.required"
            defaultMessage="Required"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.actual"
            defaultMessage="Actual"
          />
        </TableCell>
        {pvaData.stage.flowPVAData
          ? pvaData.stage.flowPVAData[
              pvaData.pvaTableData.granularity
            ].dataPointList.map((datapoint, index) => (
              <TableCell key={index}>{datapoint.metricValue}</TableCell>
            ))
          : null}
      </TableRow>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.target_pph"
            defaultMessage="Target PPH"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <FormattedMessage
            id="package_overview.actual_pph"
            defaultMessage="Actual PPH"
          />
        </TableCell>
        {shiftRange.map((timeStampVal) => {
          const time = new Date(timeStampVal);
          return (
            <TableCell key={timeStampVal}>
              {stageActualPPH[time] === -1 || !stageActualPPH[time]
                ? ""
                : stageActualPPH[time].toFixed(0)}
            </TableCell>
          );
        })}
      </TableRow>
    </Table>
  );
};

export default PackageOverviewOutbound;



// WEBPACK FOOTER //
// ./src/components/packageOverview/PackageOverviewOutbound.js